import {
  chatBubbleIcon,
  closeIcon,
  logoutIcon,
  moreVertIcon,
  personIcon,
  refreshIcon,
  uploadIcon,
} from './icons';
import { getWidgetDir, t } from './i18n';

export interface WidgetDomElements {
  container: HTMLElement;
  floatingButton: HTMLButtonElement;
  floatingButtonIcon: HTMLElement;
  launcherBadge: HTMLElement;
  panel: HTMLElement;
  header: HTMLElement;
  headerAvatar: HTMLElement;
  headerStatusDot: HTMLElement;
  headerTitle: HTMLElement;
  statusLabel: HTMLElement;
  overflowButton: HTMLButtonElement;
  overflowMenu: HTMLElement;
  endChatButton: HTMLButtonElement;
  soundToggleButton: HTMLButtonElement;
  notificationsToggleButton: HTMLButtonElement;
  closeButton: HTMLButtonElement;
  reconnectBanner: HTMLButtonElement;
  body: HTMLElement;
  footer: HTMLElement;
  dropOverlay: HTMLElement;
}

// primaryColor was deliberately removed from this interface: the widget's
// colors are a fixed part of the design (design-system/README.md), and a
// runtime accent override let a stray App Embed color setting break them
// in production. Position is layout, not identity — it stays configurable.
export interface WidgetTheme {
  position?: 'bottom-right' | 'bottom-left';
}

const WIDGET_ROOT_CLASS = 'flix-chat-widget';
const PANEL_ID = 'flix-chat-widget-panel';

// An on/off item of the header's overflow menu, complete enough to be
// announced correctly before anything has rendered its real state: a
// menuitemcheckbox without a name or aria-checked is not a valid control.
function createOverflowToggle(label: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `${WIDGET_ROOT_CLASS}__overflow-item`;
  button.setAttribute('role', 'menuitemcheckbox');
  button.setAttribute('aria-checked', 'false');
  button.setAttribute('aria-label', label);
  button.append(document.createTextNode(label));
  return button;
}

export function createWidgetDom(): WidgetDomElements {
  const container = document.createElement('div');
  container.className = WIDGET_ROOT_CLASS;
  // The widget controls its own direction rather than inheriting the host
  // page's: Arabic pages get a fully mirrored RTL layout, and an English
  // widget on an RTL page stays LTR instead of half-flipping (the bug the
  // screenshots showed: ".This conversation has ended" with a leading dot).
  container.dir = getWidgetDir();

  const floatingButton = document.createElement('button');
  floatingButton.type = 'button';
  floatingButton.className = `${WIDGET_ROOT_CLASS}__launcher`;
  floatingButton.setAttribute('aria-label', t('launcherOpen'));
  floatingButton.setAttribute('aria-expanded', 'false');
  floatingButton.setAttribute('aria-controls', PANEL_ID);

  const floatingButtonIcon = document.createElement('span');
  floatingButtonIcon.className = `${WIDGET_ROOT_CLASS}__launcher-icon`;
  floatingButtonIcon.append(chatBubbleIcon());
  floatingButton.append(floatingButtonIcon);

  // Unread-message counter — hidden whenever the count is 0 (see
  // widget.ts's renderUnreadIndicators, the only writer).
  const launcherBadge = document.createElement('span');
  launcherBadge.className = `${WIDGET_ROOT_CLASS}__launcher-badge`;
  launcherBadge.setAttribute('aria-hidden', 'true');
  launcherBadge.hidden = true;
  floatingButton.append(launcherBadge);

  const panel = document.createElement('div');
  panel.id = PANEL_ID;
  panel.className = `${WIDGET_ROOT_CLASS}__panel`;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', t('panelAria'));
  // Programmatically focusable (never in Tab order) so open() has
  // somewhere correct to send focus when there's no ready composer to
  // focus instead (login-required screen, or before a conversation
  // connects) — the standard pattern for a dialog with no obvious first
  // focusable field.
  panel.tabIndex = -1;
  panel.hidden = true;

  const header = document.createElement('div');
  header.className = `${WIDGET_ROOT_CLASS}__header`;

  const headerAvatar = document.createElement('div');
  headerAvatar.className = `${WIDGET_ROOT_CLASS}__header-avatar`;
  headerAvatar.append(personIcon());

  const headerStatusDot = document.createElement('span');
  headerStatusDot.className = `${WIDGET_ROOT_CLASS}__header-status-dot`;
  headerAvatar.append(headerStatusDot);

  const headerTitleBlock = document.createElement('div');
  headerTitleBlock.className = `${WIDGET_ROOT_CLASS}__header-title-block`;

  const headerTitle = document.createElement('div');
  headerTitle.className = `${WIDGET_ROOT_CLASS}__header-title`;
  headerTitle.textContent = t('headerTitle');

  const statusLabel = document.createElement('div');
  statusLabel.className = `${WIDGET_ROOT_CLASS}__header-subtitle`;

  headerTitleBlock.append(headerTitle, statusLabel);

  const overflowButton = document.createElement('button');
  overflowButton.type = 'button';
  overflowButton.className = `${WIDGET_ROOT_CLASS}__header-overflow`;
  overflowButton.setAttribute('aria-label', t('moreOptions'));
  overflowButton.setAttribute('aria-haspopup', 'true');
  overflowButton.setAttribute('aria-expanded', 'false');
  // Its only current menu item (End chat) only ever makes sense once a
  // conversation exists — see widget.ts's renderEndChatButton, the only
  // place this is un-hidden. Must default hidden here too: the
  // login-required path returns before renderEndChatButton ever runs, so
  // without this the button would sit permanently visible with an empty
  // menu for every logged-out visitor.
  overflowButton.hidden = true;
  overflowButton.append(moreVertIcon());

  const overflowMenu = document.createElement('div');
  overflowMenu.className = `${WIDGET_ROOT_CLASS}__overflow-menu`;
  overflowMenu.setAttribute('role', 'menu');
  overflowMenu.hidden = true;

  // Notification-sound preference. widget.ts's renderSoundToggle renders
  // the real thing (icon + label + checked state) once it knows the
  // persisted preference — but it never runs for a logged-out visitor,
  // because initialize() returns at the login prompt first. Left bare, that
  // put a checkbox item in every logged-out page with no name and no state,
  // so it is named and stated here, from birth, and re-rendered later.
  const soundToggleButton = createOverflowToggle(t('soundToggleLabel'));
  overflowMenu.append(soundToggleButton);

  // Browser-notification preference — same pattern as soundToggleButton
  // above; widget.ts's renderNotificationsToggle owns the icon, the real
  // checked state and hidden (hidden entirely when the Notifications API
  // doesn't exist at all, per the graceful-fallback requirement).
  const notificationsToggleButton = createOverflowToggle(t('notificationsToggleLabel'));
  overflowMenu.append(notificationsToggleButton);

  const endChatButton = document.createElement('button');
  endChatButton.type = 'button';
  endChatButton.className = `${WIDGET_ROOT_CLASS}__overflow-item ${WIDGET_ROOT_CLASS}__overflow-item--danger`;
  endChatButton.setAttribute('role', 'menuitem');
  endChatButton.setAttribute('aria-label', t('endChatAria'));
  endChatButton.hidden = true;
  endChatButton.append(logoutIcon(), document.createTextNode(t('endChat')));
  overflowMenu.append(endChatButton);

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = `${WIDGET_ROOT_CLASS}__header-close`;
  closeButton.setAttribute('aria-label', t('launcherClose'));
  closeButton.append(closeIcon());

  header.append(headerAvatar, headerTitleBlock, overflowButton, overflowMenu, closeButton);

  const reconnectBanner = document.createElement('button');
  reconnectBanner.type = 'button';
  reconnectBanner.className = `${WIDGET_ROOT_CLASS}__reconnect-banner`;
  reconnectBanner.hidden = true;
  const reconnectIcon = refreshIcon();
  reconnectIcon.classList.add(`${WIDGET_ROOT_CLASS}__icon--spin`);
  const reconnectText = document.createElement('span');
  reconnectText.textContent = t('statusReconnecting');
  reconnectBanner.append(reconnectIcon, reconnectText);

  const body = document.createElement('div');
  body.className = `${WIDGET_ROOT_CLASS}__body`;

  const footer = document.createElement('div');
  footer.className = `${WIDGET_ROOT_CLASS}__footer`;

  // Drag-and-drop overlay — covers the conversation area while a file is
  // dragged over the panel (see widget.ts's drag wiring). Hidden by
  // default; shown only when there's an active conversation to attach to.
  const dropOverlay = document.createElement('div');
  dropOverlay.className = `${WIDGET_ROOT_CLASS}__drop-overlay`;
  dropOverlay.hidden = true;
  const dropOverlayInner = document.createElement('div');
  dropOverlayInner.className = `${WIDGET_ROOT_CLASS}__drop-overlay-inner`;
  const dropOverlayIcon = document.createElement('span');
  dropOverlayIcon.className = `${WIDGET_ROOT_CLASS}__drop-overlay-icon`;
  dropOverlayIcon.append(uploadIcon());
  const dropOverlayTitle = document.createElement('div');
  dropOverlayTitle.className = `${WIDGET_ROOT_CLASS}__drop-overlay-title`;
  dropOverlayTitle.textContent = t('dropToAttach');
  const dropOverlayHint = document.createElement('div');
  dropOverlayHint.className = `${WIDGET_ROOT_CLASS}__drop-overlay-hint`;
  dropOverlayHint.textContent = t('dropHint');
  dropOverlayInner.append(dropOverlayIcon, dropOverlayTitle, dropOverlayHint);
  dropOverlay.append(dropOverlayInner);

  panel.append(header, reconnectBanner, body, footer, dropOverlay);
  container.append(floatingButton, panel);

  return {
    container,
    floatingButton,
    floatingButtonIcon,
    launcherBadge,
    panel,
    header,
    headerAvatar,
    headerStatusDot,
    headerTitle,
    statusLabel,
    overflowButton,
    overflowMenu,
    endChatButton,
    soundToggleButton,
    notificationsToggleButton,
    closeButton,
    reconnectBanner,
    body,
    footer,
    dropOverlay,
  };
}

// Applies the one runtime-configurable theme option left: which bottom
// corner the widget anchors to. Colors are fixed by design — see the
// WidgetTheme comment above for why the accent override was removed.
export function applyWidgetTheme(
  elements: WidgetDomElements,
  theme: WidgetTheme | undefined,
): void {
  if (theme?.position === 'bottom-left') {
    elements.container.classList.add(`${WIDGET_ROOT_CLASS}--left`);
  }
}

const STYLE_ELEMENT_ID = 'flix-chat-widget-styles';

// Mirrors styles.css exactly. tsc has no CSS loader, so the actual rule
// text is duplicated here for runtime <style> injection — styles.css is
// the readable source a future bundler step would load instead.
const WIDGET_STYLES = `
.flix-chat-widget {
  --fcw-bg: #0b0908;
  --fcw-surface: #141110;
  --fcw-surface-2: #100d0c;
  --fcw-surface-header: #161211;
  --fcw-surface-raised: #1b1613;
  --fcw-surface-input: #1d1815;
  --fcw-bubble-staff: #221d1a;
  --fcw-border: rgba(255, 255, 255, .06);
  --fcw-border-strong: rgba(255, 255, 255, .09);

  --flix-chat-accent: #F5921F;
  --fcw-accent-2: #E4640F;
  --fcw-accent-hover-1: #ffa235;
  --fcw-accent-hover-2: #f57320;
  --fcw-accent-pressed: #d9770e;
  --fcw-accent-disabled-bg: rgba(245, 130, 30, .22);
  --fcw-accent-disabled-fg: #a5734a;
  --fcw-accent-soft-bg: rgba(245, 130, 30, .13);

  --fcw-text: #F2ECE6;
  --fcw-text-soft: #e9e2db;
  --fcw-text-softer: #cdc3bb;
  --fcw-text-muted: #8a807a;
  --fcw-text-mutest: #6b625b;
  --fcw-text-faint: #5f564f;
  --fcw-on-accent: #150d05;

  --fcw-success: #3fbf6e;
  --fcw-danger: #ef5350;
  --fcw-danger-bg: rgba(239, 83, 80, .14);
  --fcw-danger-border: rgba(239, 83, 80, .35);
  --fcw-danger-text: #f0d9d6;
  --fcw-danger-text-2: #ef7a77;

  --fcw-space-xs: 4px;
  --fcw-space-sm: 8px;
  --fcw-space-md: 12px;
  --fcw-space-lg: 16px;
  --fcw-space-xl: 24px;
  --fcw-space-2xl: 32px;

  --fcw-radius-sm: 8px;
  --fcw-radius-md: 12px;
  --fcw-radius-lg: 16px;
  --fcw-radius-xl: 20px;
  --fcw-radius-full: 999px;
  --fcw-radius-panel: 22px;
  --fcw-radius-bubble: 15px;
  --fcw-radius-bubble-tail: 5px;
  --fcw-radius-launcher: 18px;
  --fcw-radius-empty-icon: 18px;
  --fcw-radius-composer-send: 10px;

  --fcw-shadow-e1: 0 4px 12px rgba(0, 0, 0, .35);
  --fcw-shadow-e2: 0 16px 40px rgba(0, 0, 0, .5);
  --fcw-shadow-e3: 0 32px 70px rgba(0, 0, 0, .6);
  --fcw-shadow-accent: 0 4px 12px rgba(245, 130, 30, .3);
  --fcw-shadow-accent-hover: 0 6px 16px rgba(245, 130, 30, .45);
  --fcw-shadow-cta: 0 6px 16px rgba(245, 130, 30, .28);
  --fcw-shadow-launcher: 0 10px 26px rgba(245, 130, 30, .34);
  --fcw-shadow-launcher-hover: 0 16px 34px rgba(245, 130, 30, .5);
  --fcw-shadow-launcher-open: 0 10px 26px rgba(0, 0, 0, .4);

  --fcw-border-composer: rgba(255, 255, 255, .08);
  --fcw-accent-disabled-bg-composer: rgba(245, 130, 30, .2);
  --fcw-accent-soft-bg-empty: rgba(245, 130, 30, .1);

  --fcw-motion-fast: 120ms;
  --fcw-motion-base: 180ms;
  --fcw-motion-panel: 250ms;
  --fcw-ease-standard: cubic-bezier(.2, .8, .2, 1);
  --fcw-ease-exit: cubic-bezier(.4, 0, 1, 1);

  --fcw-z-launcher: 2147483000;
  --fcw-z-panel: 2147483001;
  --fcw-z-menu: 2147483002;
  --fcw-z-dialog: 2147483003;

  font-family: Inter, -apple-system, BlinkMacSystemFont, sans-serif;
}

.flix-chat-widget__icon {
  width: 1em;
  height: 1em;
  display: inline-block;
  vertical-align: middle;
}

.flix-chat-widget__icon--spin {
  animation: flix-chat-spin 1s linear infinite;
}

@keyframes flix-chat-spin {
  to { transform: rotate(360deg); }
}

@keyframes flix-chat-fade-rise {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes flix-chat-float-up {
  from { opacity: 0; transform: translateY(8px) scale(.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* The exact reverse of flix-chat-float-up above, using the --fcw-ease-exit
   token (already defined for this purpose, previously unused). Applied via
   .flix-chat-widget__panel--closing, added right before the panel is
   hidden so closing feels symmetric to opening instead of an instant cut. */
@keyframes flix-chat-float-down {
  from { opacity: 1; transform: translateY(0) scale(1); }
  to { opacity: 0; transform: translateY(8px) scale(.98); }
}

@keyframes flix-chat-typing-dot {
  0%, 60%, 100% { opacity: .35; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-2px); }
}

@keyframes flix-chat-shimmer {
  from { background-position: 320px 0; }
  to { background-position: -320px 0; }
}

@keyframes flix-chat-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.flix-chat-widget__launcher {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: var(--fcw-z-launcher);
  width: 58px;
  height: 58px;
  border: none;
  border-radius: var(--fcw-radius-launcher);
  background: linear-gradient(150deg, var(--flix-chat-accent), var(--fcw-accent-2));
  color: var(--fcw-on-accent);
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: var(--fcw-shadow-launcher);
  transition: transform var(--fcw-motion-base) var(--fcw-ease-standard), box-shadow var(--fcw-motion-base);
}

.flix-chat-widget__launcher:hover {
  transform: translateY(-2px);
  box-shadow: var(--fcw-shadow-launcher-hover);
}

/* Open state: the launcher becomes the panel's close affordance and swaps
   from the accent gradient to a neutral dark surface — matches the
   "Greeting teaser" example's close-icon launcher in the design, not the
   default closed-state gradient button. */
.flix-chat-widget__launcher--open {
  background: #1b1613;
  border: 1px solid rgba(255, 255, 255, .09);
  color: var(--fcw-text-soft);
  box-shadow: var(--fcw-shadow-launcher-open);
}

.flix-chat-widget__launcher--open:hover {
  box-shadow: var(--fcw-shadow-launcher-open);
}

.flix-chat-widget__launcher-icon {
  font-size: 27px;
  display: grid;
  place-items: center;
}

.flix-chat-widget__launcher--open .flix-chat-widget__launcher-icon {
  font-size: 25px;
}

.flix-chat-widget__launcher-badge {
  position: absolute;
  top: -4px;
  inset-inline-end: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: var(--fcw-radius-full);
  background: var(--fcw-danger);
  color: #fff;
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--fcw-surface);
  pointer-events: none;
}

/* display:flex above is an author rule, so it overrides the UA
   [hidden]{display:none} — without this the badge stays a visible empty
   red circle even when hidden with a 0 count. Re-assert none for [hidden]. */
.flix-chat-widget__launcher-badge[hidden] {
  display: none;
}

.flix-chat-widget--left .flix-chat-widget__launcher {
  right: auto;
  left: 16px;
}

/* ===== Greeting teaser ===== */

.flix-chat-widget__teaser {
  position: fixed;
  right: 16px;
  bottom: 84px;
  z-index: var(--fcw-z-panel);
  width: 300px;
  max-width: calc(100vw - 32px);
}

.flix-chat-widget--left .flix-chat-widget__teaser {
  right: auto;
  left: 16px;
}

.flix-chat-widget__teaser--visible {
  animation: flix-chat-float-up var(--fcw-motion-panel) var(--fcw-ease-standard) both;
}

.flix-chat-widget__teaser--hiding {
  animation: flix-chat-float-down var(--fcw-motion-panel) var(--fcw-ease-exit) both;
}

.flix-chat-widget__teaser-bubble {
  position: relative;
  background: var(--fcw-surface-raised);
  border: 1px solid var(--fcw-border-strong);
  border-radius: var(--fcw-radius-lg);
  box-shadow: var(--fcw-shadow-e2);
  padding: var(--fcw-space-md);
}

.flix-chat-widget__teaser-main {
  display: flex;
  align-items: center;
  gap: var(--fcw-space-md);
  width: 100%;
  padding: 0;
  padding-inline-end: var(--fcw-space-lg);
  background: none;
  border: none;
  cursor: pointer;
  text-align: start;
  color: inherit;
  font: inherit;
}

.flix-chat-widget__teaser-main:focus-visible {
  outline: 2px solid var(--flix-chat-accent);
  outline-offset: 3px;
  border-radius: var(--fcw-radius-md);
}

.flix-chat-widget__teaser-avatar {
  position: relative;
  width: 38px;
  height: 38px;
  flex: none;
  border-radius: var(--fcw-radius-full);
  background: linear-gradient(150deg, #3a2f27, #211a15);
  color: #F5b070;
  display: grid;
  place-items: center;
  font-size: 15px;
  font-weight: 600;
}

.flix-chat-widget__teaser-avatar-image {
  width: 100%;
  height: 100%;
  border-radius: var(--fcw-radius-full);
  object-fit: cover;
}

.flix-chat-widget__teaser-status-dot {
  position: absolute;
  inset-inline-end: -1px;
  bottom: -1px;
  width: 11px;
  height: 11px;
  border-radius: var(--fcw-radius-full);
  background: var(--fcw-text-mutest);
  border: 2.5px solid var(--fcw-surface-raised);
}

.flix-chat-widget__teaser-status-dot--online {
  background: var(--fcw-success);
}

.flix-chat-widget__teaser-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.flix-chat-widget__teaser-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--fcw-text-softer);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flix-chat-widget__teaser-message {
  font-size: 13px;
  line-height: 1.4;
  color: var(--fcw-text-soft);
  white-space: pre-line;
}

.flix-chat-widget__teaser-close {
  position: absolute;
  top: 6px;
  inset-inline-end: 6px;
  width: 22px;
  height: 22px;
  border-radius: var(--fcw-radius-full);
  background: none;
  border: none;
  color: var(--fcw-text-mutest);
  display: grid;
  place-items: center;
  font-size: 13px;
  cursor: pointer;
  transition: color var(--fcw-motion-fast), background var(--fcw-motion-fast);
}

.flix-chat-widget__teaser-close:hover {
  background: rgba(255, 255, 255, .06);
  color: var(--fcw-text-soft);
}

.flix-chat-widget__teaser-close:focus-visible {
  outline: 2px solid var(--flix-chat-accent);
  outline-offset: 1px;
}

.flix-chat-widget__teaser-arrow {
  position: absolute;
  bottom: -6px;
  right: 20px;
  width: 12px;
  height: 12px;
  background: var(--fcw-surface-raised);
  border-right: 1px solid var(--fcw-border-strong);
  border-bottom: 1px solid var(--fcw-border-strong);
  transform: rotate(45deg);
}

/* The arrow points at the launcher, which is corner-anchored (physical
   right/left), not text-direction-anchored — it must NOT flip with RTL the
   way inset-inline-end/border-inline-end would. Only the --left modifier
   (a different corner) moves it, mirroring which two edges are bordered. */
.flix-chat-widget--left .flix-chat-widget__teaser-arrow {
  right: auto;
  left: 20px;
  border-right: none;
  border-left: 1px solid var(--fcw-border-strong);
}

.flix-chat-widget__panel {
  position: fixed;
  right: 16px;
  bottom: 84px;
  z-index: var(--fcw-z-panel);
  width: 384px;
  max-width: calc(100vw - 32px);
  height: 616px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: var(--fcw-surface);
  color: var(--fcw-text);
  border: 1px solid var(--fcw-border-strong);
  border-radius: var(--fcw-radius-panel);
  overflow: hidden;
  box-shadow: var(--fcw-shadow-e3);
  animation: flix-chat-float-up var(--fcw-motion-panel) var(--fcw-ease-standard) both;
}

.flix-chat-widget--left .flix-chat-widget__panel {
  right: auto;
  left: 16px;
}

.flix-chat-widget__panel[hidden] {
  display: none;
}

.flix-chat-widget__panel--closing {
  animation: flix-chat-float-down var(--fcw-motion-panel) var(--fcw-ease-exit) both;
}

/* ===== Drag-and-drop overlay ===== */

.flix-chat-widget__drop-overlay {
  position: absolute;
  inset: 0;
  z-index: var(--fcw-z-menu);
  padding: var(--fcw-space-md);
  background: rgba(11, 9, 8, .82);
  border-radius: var(--fcw-radius-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: flix-chat-fade-rise var(--fcw-motion-base) ease both;
}

.flix-chat-widget__drop-overlay[hidden] {
  display: none;
}

.flix-chat-widget__drop-overlay-inner {
  width: 100%;
  height: 100%;
  border: 2px dashed var(--flix-chat-accent);
  border-radius: var(--fcw-radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--fcw-space-sm);
  text-align: center;
  padding: var(--fcw-space-lg);
  pointer-events: none;
}

.flix-chat-widget__drop-overlay-icon {
  font-size: 34px;
  color: var(--flix-chat-accent);
  display: grid;
  place-items: center;
}

.flix-chat-widget__drop-overlay-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--fcw-text);
}

.flix-chat-widget__drop-overlay-hint {
  font-size: 12px;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__header {
  display: flex;
  align-items: center;
  gap: 11px;
  flex-shrink: 0;
  padding-block: 13px;
  padding-inline: 14px 12px;
  background: var(--fcw-surface-header);
  border-bottom: 1px solid var(--fcw-border);
}

.flix-chat-widget__header-avatar {
  position: relative;
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: var(--fcw-radius-full);
  background: linear-gradient(150deg, #3a2f27, #211a15);
  color: #F5b070;
  display: grid;
  place-items: center;
  font-size: 16px;
}

.flix-chat-widget__header-status-dot {
  position: absolute;
  inset-inline-end: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: var(--fcw-radius-full);
  background: var(--fcw-text-mutest);
  border: 2.5px solid var(--fcw-surface-header);
  transition: background var(--fcw-motion-base);
}

.flix-chat-widget__header-status-dot--online {
  background: var(--fcw-success);
}

.flix-chat-widget__header-title-block {
  flex: 1 1 auto;
  min-width: 0;
}

.flix-chat-widget__header-title {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flix-chat-widget__header-subtitle {
  font-size: 12px;
  line-height: 1.3;
  color: var(--fcw-text-muted);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flix-chat-widget__header--clickable {
  cursor: pointer;
  transition: background var(--fcw-motion-base);
}

.flix-chat-widget__header--clickable:hover {
  background: #1a1613;
}

.flix-chat-widget__header-overflow,
.flix-chat-widget__header-close {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: var(--fcw-radius-md);
  background: transparent;
  border: none;
  color: var(--fcw-text-muted);
  display: grid;
  place-items: center;
  cursor: pointer;
  font-size: 19px;
  transition: background var(--fcw-motion-fast), color var(--fcw-motion-fast);
}

.flix-chat-widget__header-overflow:hover,
.flix-chat-widget__header-close:hover {
  background: rgba(255, 255, 255, .06);
  color: var(--fcw-text);
}

.flix-chat-widget__header-overflow[hidden] {
  display: none;
}

.flix-chat-widget__overflow-menu {
  position: absolute;
  top: 62px;
  inset-inline-end: 44px;
  z-index: var(--fcw-z-menu);
  width: 180px;
  background: var(--fcw-surface-input);
  border: 1px solid var(--fcw-border-strong);
  border-radius: var(--fcw-radius-md);
  padding: 6px;
  box-shadow: var(--fcw-shadow-e2);
  animation: flix-chat-float-up var(--fcw-motion-fast) ease both;
}

.flix-chat-widget__overflow-menu[hidden] {
  display: none;
}

.flix-chat-widget__overflow-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 9px;
  background: transparent;
  border: none;
  color: var(--fcw-text-soft);
  font: 500 13px/1 Inter, sans-serif;
  cursor: pointer;
  text-align: start;
  transition: background var(--fcw-motion-fast);
}

.flix-chat-widget__overflow-item:hover {
  background: rgba(255, 255, 255, .06);
}

.flix-chat-widget__overflow-item--danger {
  color: var(--fcw-danger-text-2);
}

.flix-chat-widget__overflow-item--danger:hover {
  background: var(--fcw-danger-bg);
}

.flix-chat-widget__overflow-item[hidden] {
  display: none;
}

.flix-chat-widget__reconnect-banner {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 10px 15px;
  background: rgba(245, 130, 30, .1);
  border: none;
  border-bottom: 1px solid rgba(245, 130, 30, .22);
  color: #F5b070;
  font: 500 12px/1.3 Inter, sans-serif;
  cursor: pointer;
  text-align: start;
  flex-shrink: 0;
}

.flix-chat-widget__reconnect-banner[hidden] {
  display: none;
}

.flix-chat-widget__reconnect-banner .flix-chat-widget__icon {
  font-size: 16px;
}

.flix-chat-widget__body {
  flex: 1 1 auto;
  overflow: hidden;
  background: var(--fcw-surface-2);
}

.flix-chat-widget__footer {
  flex-shrink: 0;
  min-height: 8px;
  background: var(--fcw-surface);
}

.flix-chat-widget__footer[hidden] {
  display: none;
}

/* ===== Login required ===== */

.flix-chat-widget__login-required {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 28px;
  text-align: center;
}

.flix-chat-widget__login-required-icon {
  width: 60px;
  height: 60px;
  border-radius: var(--fcw-radius-xl);
  background: var(--fcw-accent-soft-bg);
  color: var(--flix-chat-accent);
  display: grid;
  place-items: center;
  font-size: 28px;
  margin-bottom: 6px;
}

.flix-chat-widget__login-required-title {
  margin: 0;
  font: 700 18px/1.3 Inter, sans-serif;
}

.flix-chat-widget__login-required-description {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--fcw-text-muted);
  max-width: 260px;
}

.flix-chat-widget__login-required-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 230px;
  margin-top: 8px;
}

.flix-chat-widget__login-required-login,
.flix-chat-widget__login-required-register {
  display: block;
  box-sizing: border-box;
  padding: 12px;
  border-radius: var(--fcw-radius-md);
  font: 700 13px/1 Inter, sans-serif;
  text-decoration: none;
  transition: transform var(--fcw-motion-base), background var(--fcw-motion-base);
}

.flix-chat-widget__login-required-login {
  background: linear-gradient(150deg, var(--flix-chat-accent), var(--fcw-accent-2));
  color: var(--fcw-on-accent);
  box-shadow: var(--fcw-shadow-cta);
}

.flix-chat-widget__login-required-login:hover {
  transform: translateY(-2px);
}

.flix-chat-widget__login-required-register {
  border: 1px solid var(--fcw-border-strong);
  color: var(--fcw-text-soft);
  background: transparent;
}

.flix-chat-widget__login-required-register:hover {
  background: rgba(255, 255, 255, .06);
}

/* ===== Name required (one-time, per customer) ===== */

.flix-chat-widget__name-required {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 28px;
  text-align: center;
}

.flix-chat-widget__name-required-icon {
  width: 60px;
  height: 60px;
  border-radius: var(--fcw-radius-xl);
  background: var(--fcw-accent-soft-bg);
  color: var(--flix-chat-accent);
  display: grid;
  place-items: center;
  font-size: 28px;
  margin-bottom: 6px;
}

.flix-chat-widget__name-required-title {
  margin: 0;
  font: 700 18px/1.3 Inter, sans-serif;
}

.flix-chat-widget__name-required-description {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--fcw-text-muted);
  max-width: 260px;
}

.flix-chat-widget__name-required-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 230px;
  margin-top: 8px;
}

.flix-chat-widget__name-required-input {
  box-sizing: border-box;
  width: 100%;
  padding: 11px 14px;
  border-radius: var(--fcw-radius-md);
  border: 1px solid var(--fcw-border-composer);
  background: var(--fcw-surface-input);
  color: var(--fcw-text);
  font: 400 14px/1.4 Inter, sans-serif;
  text-align: center;
  transition: border-color var(--fcw-motion-base), box-shadow var(--fcw-motion-base);
}

.flix-chat-widget__name-required-input::placeholder {
  color: var(--fcw-text-faint);
}

.flix-chat-widget__name-required-input:focus-visible {
  outline: none;
  border-color: rgba(245, 130, 30, .5);
  box-shadow: 0 0 0 3px rgba(245, 130, 30, .1);
}

.flix-chat-widget__name-required-error {
  margin: 0;
  color: var(--fcw-danger);
  font-size: 12px;
}

.flix-chat-widget__name-required-error[hidden] {
  display: none;
}

.flix-chat-widget__name-required-submit {
  box-sizing: border-box;
  padding: 12px;
  border: none;
  border-radius: var(--fcw-radius-md);
  background: linear-gradient(150deg, var(--flix-chat-accent), var(--fcw-accent-2));
  color: var(--fcw-on-accent);
  font: 700 13px/1 Inter, sans-serif;
  box-shadow: var(--fcw-shadow-cta);
  cursor: pointer;
  transition: transform var(--fcw-motion-base);
}

.flix-chat-widget__name-required-submit:hover:not(:disabled) {
  transform: translateY(-2px);
}

/* ===== Conversation area ===== */

.flix-chat-widget__messages {
  height: 100%;
  box-sizing: border-box;
  padding: 16px 14px 6px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.flix-chat-widget__messages-loading,
.flix-chat-widget__messages-empty,
.flix-chat-widget__messages-error,
.flix-chat-widget__messages-ended {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  color: var(--fcw-text-muted);
  font-size: 13px;
  line-height: 1.55;
}

.flix-chat-widget__messages-loading {
  align-items: stretch;
  gap: 14px;
  padding: 18px 15px;
}

.flix-chat-widget__messages-loading-row {
  display: flex;
  gap: 9px;
  align-items: flex-end;
}

.flix-chat-widget__messages-loading-row--customer {
  justify-content: flex-end;
}

.flix-chat-widget__messages-loading-avatar {
  width: 26px;
  height: 26px;
  border-radius: var(--fcw-radius-full);
  background: var(--fcw-bubble-staff);
  flex: none;
}

.flix-chat-widget__messages-loading-bubble {
  height: 34px;
  border-radius: var(--fcw-radius-bubble);
  background: linear-gradient(90deg, #1c1714 25%, #2a2320 37%, #1c1714 63%);
  background-size: 320px 100%;
  animation: flix-chat-shimmer 1.4s infinite linear;
}

.flix-chat-widget__messages-empty-icon,
.flix-chat-widget__messages-error-icon {
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  font-size: 28px;
}

.flix-chat-widget__messages-empty-icon {
  border-radius: var(--fcw-radius-empty-icon);
  background: var(--fcw-accent-soft-bg-empty);
  color: var(--flix-chat-accent);
  margin-bottom: 16px;
  animation: flix-chat-bob 3.5s ease-in-out infinite;
}

.flix-chat-widget__messages-error-icon {
  border-radius: var(--fcw-radius-lg);
  background: var(--fcw-danger-bg);
  color: var(--fcw-danger);
  margin-bottom: 15px;
}

.flix-chat-widget__messages-empty-title,
.flix-chat-widget__messages-error-title,
.flix-chat-widget__messages-ended-title {
  font: 700 15px/1.3 Inter, sans-serif;
  color: var(--fcw-text);
  margin-bottom: 6px;
}

.flix-chat-widget__messages-empty-title {
  font-size: 16px;
}

.flix-chat-widget__messages-error-title,
.flix-chat-widget__messages-ended-title {
  margin-bottom: 18px;
}

/* Direct children only: the ended state now also contains the recent-
   conversations rows (themselves <button>s) which must NOT pick up the
   accent-CTA treatment reserved for Start new chat / Try again. */
.flix-chat-widget__messages-error > button,
.flix-chat-widget__messages-ended > button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: linear-gradient(150deg, var(--flix-chat-accent), var(--fcw-accent-2));
  border: none;
  color: var(--fcw-on-accent);
  padding: 10px 18px;
  border-radius: var(--fcw-radius-md);
  font: 700 13px/1 Inter, sans-serif;
  cursor: pointer;
  box-shadow: var(--fcw-shadow-cta);
  transition: transform var(--fcw-motion-base);
}

.flix-chat-widget__messages-error > button:hover,
.flix-chat-widget__messages-ended > button:hover {
  transform: translateY(-2px);
}

.flix-chat-widget__date-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 2px 0 8px;
  flex-shrink: 0;
}

.flix-chat-widget__date-divider-line {
  flex: 1;
  height: 1px;
  background: var(--fcw-border);
}

.flix-chat-widget__date-divider-label {
  font: 600 10px/1 Inter, sans-serif;
  letter-spacing: .06em;
  color: var(--fcw-text-mutest);
  text-transform: uppercase;
}

/* ===== Message bubbles ===== */

.flix-chat-widget__message {
  display: flex;
  gap: 9px;
  align-items: flex-end;
  max-width: 100%;
  /* Flex items default to min-width: auto (their unwrapped content size),
     which overrides overflow-wrap/break-word below and lets a long
     unbroken string (a URL, an order number) push the bubble past the
     panel edge. min-width: 0 lets the item actually shrink to the
     container. */
  min-width: 0;
  animation: flix-chat-fade-rise var(--fcw-motion-base) ease both;
}

.flix-chat-widget__message--customer {
  flex-direction: row-reverse;
}

.flix-chat-widget__message--system {
  justify-content: center;
  text-align: center;
  font-style: italic;
  color: var(--fcw-text-muted);
  font-size: 12px;
}

.flix-chat-widget__message--staff-joined {
  align-items: center;
  gap: 6px;
}

.flix-chat-widget__typing-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  /* 26px avatar + 9px gap — aligns with where a staff bubble's own text
     starts, so this reads as "the same person who'd be typing here". */
  padding-inline-start: 35px;
  color: var(--fcw-text-mutest);
  font-size: 12px;
  font-style: italic;
}

.flix-chat-widget__typing-indicator-dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.flix-chat-widget__typing-indicator-dots span {
  width: 5px;
  height: 5px;
  border-radius: var(--fcw-radius-full);
  background: var(--fcw-text-mutest);
  animation: flix-chat-typing-dot 1.2s ease-in-out infinite;
}

.flix-chat-widget__typing-indicator-dots span:nth-child(2) {
  animation-delay: .15s;
}

.flix-chat-widget__typing-indicator-dots span:nth-child(3) {
  animation-delay: .3s;
}

.flix-chat-widget__message-system-avatar {
  width: 20px;
  height: 20px;
  flex: none;
  border-radius: var(--fcw-radius-full);
  object-fit: cover;
}

.flix-chat-widget__message-avatar {
  width: 26px;
  height: 26px;
  flex: none;
  border-radius: var(--fcw-radius-full);
  background: linear-gradient(150deg, #3a2f27, #211a15);
  color: #F5b070;
  display: grid;
  place-items: center;
  font: 700 10px/1 Inter, sans-serif;
  visibility: hidden;
}

.flix-chat-widget__message-avatar--visible {
  visibility: visible;
}

.flix-chat-widget__message-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
  max-width: 80%;
  min-width: 0;
}

.flix-chat-widget__message--customer .flix-chat-widget__message-group {
  align-items: flex-end;
}

.flix-chat-widget__message-body {
  box-sizing: border-box;
  padding: 9px 12px;
  border-radius: var(--fcw-radius-bubble);
  border-end-start-radius: var(--fcw-radius-bubble-tail);
  background: var(--fcw-bubble-staff);
  color: var(--fcw-text-soft);
  font: 400 14px/1.5 Inter, sans-serif;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  /* .message-group uses align-items: flex-end to right-align customer
     bubbles, which switches its children off the default stretch
     behavior — without an explicit cap here, this bubble sizes to its own
     unwrapped content instead of its flex-item parent's (already
     properly-constrained) width, defeating overflow-wrap above entirely
     for long unbroken strings like a URL. box-sizing: border-box makes the
     cap exact — otherwise the padding above is added on top of it. */
  max-width: 100%;
}

.flix-chat-widget__message--customer .flix-chat-widget__message-body {
  border-end-start-radius: var(--fcw-radius-bubble);
  border-end-end-radius: var(--fcw-radius-bubble-tail);
  background: linear-gradient(165deg, var(--flix-chat-accent), #E96D11);
  color: var(--fcw-on-accent);
  font-weight: 500;
  box-shadow: 0 4px 14px rgba(233, 109, 17, .16);
}

.flix-chat-widget__message-attachments {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.flix-chat-widget__message-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
}

.flix-chat-widget__message--customer .flix-chat-widget__message-meta {
  justify-content: flex-end;
}

.flix-chat-widget__message-timestamp {
  font: 400 11px/1 Inter, sans-serif;
  color: var(--fcw-text-faint);
}

.flix-chat-widget__message-automated {
  padding: 2px 6px;
  border: 1px solid currentColor;
  border-radius: 999px;
  font-family: inherit;
  font-size: 10px;
  font-weight: 500;
  line-height: 1;
  color: var(--fcw-text-faint);
}

.flix-chat-widget__message-body a {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  overflow-wrap: anywhere;
}

.flix-chat-widget__message-status {
  font-size: 12px;
  line-height: 1;
}

.flix-chat-widget__message-status--sent {
  color: var(--flix-chat-accent);
}

/* On its way to support (a clock), being checked (a clock and words), or
   not delivered (a warning, words and "send again" — never colour alone).
   The last two take their own line under the time. */
.flix-chat-widget__message-meta {
  flex-wrap: wrap;
}

.flix-chat-widget__message-status--sending,
.flix-chat-widget__message-status--review,
.flix-chat-widget__message-status--failed {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.flix-chat-widget__message-status--sending {
  color: var(--fcw-text-faint);
}

.flix-chat-widget__message-status--review,
.flix-chat-widget__message-status--failed {
  flex-basis: 100%;
  flex-wrap: wrap;
  justify-content: flex-end;
  font-size: 11px;
  line-height: 1.4;
}

.flix-chat-widget__message-status--review {
  color: var(--fcw-text-muted);
}

.flix-chat-widget__message-status--failed {
  font-weight: 500;
  color: var(--fcw-danger-text-2);
}

.flix-chat-widget__message-resend {
  padding: 3px 10px;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.flix-chat-widget__message-resend:hover {
  background: var(--fcw-danger-bg);
}

.flix-chat-widget__message-resend:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.flix-chat-widget__message-resend:disabled {
  opacity: .6;
  cursor: default;
}

/* Messages from the customer's previous chat (its support channel deleted)
   that never reached support: each with one press to send it here. */
.flix-chat-widget__carry-over {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0 4px;
  padding: 10px 12px;
  border: 1px solid var(--fcw-danger-border);
  border-radius: 12px;
  background: var(--fcw-danger-bg);
}

.flix-chat-widget__carry-over-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--fcw-danger-text-2);
}

.flix-chat-widget__carry-over-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.flix-chat-widget__carry-over-text {
  flex: 1 1 auto;
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  font-size: 13px;
  line-height: 1.45;
  color: var(--fcw-text);
}

.flix-chat-widget__carry-over-files {
  margin-inline-start: 6px;
  white-space: nowrap;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__carry-over-send {
  flex: 0 0 auto;
  padding: 4px 10px;
  border: 1px solid var(--fcw-danger-text-2);
  border-radius: 999px;
  background: transparent;
  color: var(--fcw-danger-text-2);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

.flix-chat-widget__carry-over-send:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.flix-chat-widget__carry-over-send:disabled {
  opacity: .6;
  cursor: default;
}

/* ===== Message interactions: reply quote, reactions, actions menu ===== */

.flix-chat-widget__message-reply-quote {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 4px 10px;
  margin-bottom: 4px;
  border-inline-start: 3px solid var(--flix-chat-accent);
  border-radius: var(--fcw-radius-sm);
  background: rgba(255, 255, 255, .05);
  max-width: 100%;
}

.flix-chat-widget__message--customer .flix-chat-widget__message-reply-quote {
  background: rgba(0, 0, 0, .16);
  border-inline-start-color: rgba(255, 255, 255, .5);
}

.flix-chat-widget__message-reply-quote-who {
  font-size: 11px;
  font-weight: 700;
  color: var(--flix-chat-accent);
}

.flix-chat-widget__message--customer .flix-chat-widget__message-reply-quote-who {
  color: inherit;
  opacity: .9;
}

.flix-chat-widget__message-reply-quote-snippet {
  font-size: 12px;
  color: var(--fcw-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

.flix-chat-widget__message--customer .flix-chat-widget__message-reply-quote-snippet {
  color: inherit;
  opacity: .75;
}

.flix-chat-widget__message-reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.flix-chat-widget__message-reactions[hidden] {
  display: none;
}

.flix-chat-widget__message--customer .flix-chat-widget__message-reactions {
  justify-content: flex-end;
}

.flix-chat-widget__message-reaction-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: var(--fcw-radius-full);
  border: 1px solid var(--fcw-border);
  background: var(--fcw-surface-raised);
  color: var(--fcw-text-soft);
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
  transition: background var(--fcw-motion-fast), border-color var(--fcw-motion-fast);
}

.flix-chat-widget__message-reaction-pill:hover {
  border-color: var(--fcw-border-strong);
}

.flix-chat-widget__message-reaction-pill--mine {
  background: var(--fcw-accent-soft-bg);
  border-color: var(--flix-chat-accent);
  color: var(--flix-chat-accent);
}

.flix-chat-widget__message-reaction-count {
  font-weight: 600;
}

.flix-chat-widget__msg-menu {
  position: fixed;
  z-index: var(--fcw-z-dialog);
  min-width: 180px;
  padding: 6px;
  border-radius: var(--fcw-radius-md);
  background: var(--fcw-surface-raised);
  border: 1px solid var(--fcw-border-strong);
  box-shadow: var(--fcw-shadow-e2);
  animation: flix-chat-fade-rise var(--fcw-motion-base) ease both;
}

.flix-chat-widget__msg-menu[hidden] {
  display: none;
}

.flix-chat-widget__msg-menu-reactions {
  display: flex;
  gap: 2px;
  padding: 2px 2px 6px;
  border-bottom: 1px solid var(--fcw-border);
  margin-bottom: 4px;
}

.flix-chat-widget__msg-menu-reaction {
  flex: 1;
  height: 34px;
  border-radius: var(--fcw-radius-sm);
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  transition: background var(--fcw-motion-fast), transform var(--fcw-motion-fast);
}

.flix-chat-widget__msg-menu-reaction:hover {
  background: rgba(255, 255, 255, .06);
  transform: scale(1.15);
}

.flix-chat-widget__msg-menu-reaction--active {
  background: var(--fcw-accent-soft-bg);
}

.flix-chat-widget__msg-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: var(--fcw-radius-sm);
  background: transparent;
  color: var(--fcw-text-soft);
  font: inherit;
  font-size: 13px;
  text-align: start;
  cursor: pointer;
  transition: background var(--fcw-motion-fast);
}

.flix-chat-widget__msg-menu-item:hover {
  background: rgba(255, 255, 255, .06);
}

.flix-chat-widget__msg-menu-item--danger {
  color: var(--fcw-danger-text-2);
}

.flix-chat-widget__msg-menu-item .flix-chat-widget__icon {
  font-size: 17px;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__msg-menu-item--danger .flix-chat-widget__icon {
  color: var(--fcw-danger-text-2);
}

.flix-chat-widget__composer-reply-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  margin-bottom: 8px;
  border-radius: var(--fcw-radius-md);
  border-inline-start: 3px solid var(--flix-chat-accent);
  background: var(--fcw-surface-raised);
}

.flix-chat-widget__composer-reply-preview-icon {
  flex: none;
  font-size: 15px;
  color: var(--flix-chat-accent);
  display: grid;
  place-items: center;
}

.flix-chat-widget__composer-reply-preview-text {
  flex: 1;
  min-width: 0;
}

.flix-chat-widget__composer-reply-preview-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--flix-chat-accent);
}

.flix-chat-widget__composer-reply-preview-snippet {
  font-size: 12px;
  color: var(--fcw-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flix-chat-widget__composer-reply-preview-cancel {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: var(--fcw-radius-full);
  border: none;
  background: transparent;
  color: var(--fcw-text-muted);
  font-size: 15px;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.flix-chat-widget__composer-reply-preview-cancel:hover {
  background: rgba(255, 255, 255, .06);
  color: var(--fcw-text-soft);
}

/* ===== Attachments ===== */

.flix-chat-widget__attachment {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 11px;
  border-radius: var(--fcw-radius-lg);
  background: var(--fcw-bubble-staff);
  border: 1px solid var(--fcw-border);
  font-size: 12px;
  max-width: 100%;
  min-width: 200px;
}

.flix-chat-widget__attachment--error {
  border-color: var(--fcw-danger-border);
  background: rgba(239, 83, 80, .08);
}

.flix-chat-widget__attachment-icon {
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: var(--fcw-radius-md);
  background: var(--fcw-accent-soft-bg);
  color: var(--flix-chat-accent);
  display: grid;
  place-items: center;
  font-size: 19px;
}

.flix-chat-widget__attachment--error .flix-chat-widget__attachment-icon {
  background: var(--fcw-danger-bg);
  color: var(--fcw-danger);
}

.flix-chat-widget__attachment-meta {
  flex: 1;
  min-width: 0;
}

.flix-chat-widget__attachment-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 600 13px/1.3 Inter, sans-serif;
  color: var(--fcw-text-soft);
}

.flix-chat-widget__attachment-size {
  display: block;
  margin-top: 2px;
  font: 400 11px/1.3 Inter, sans-serif;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__attachment-status {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__attachment-progress {
  margin-top: 6px;
  height: 4px;
  border-radius: var(--fcw-radius-full);
  background: rgba(255, 255, 255, .1);
  overflow: hidden;
}

.flix-chat-widget__attachment-progress[hidden] {
  display: none;
}

.flix-chat-widget__attachment-progress-fill {
  height: 100%;
  width: 0%;
  border-radius: var(--fcw-radius-full);
  background: linear-gradient(90deg, var(--flix-chat-accent), var(--fcw-accent-2));
  transition: width var(--fcw-motion-fast) linear;
}

.flix-chat-widget__attachment-retry,
.flix-chat-widget__attachment-remove {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: var(--fcw-radius-sm);
  background: rgba(255, 255, 255, .05);
  border: none;
  color: var(--fcw-text-softer);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background var(--fcw-motion-fast), color var(--fcw-motion-fast);
}

.flix-chat-widget__attachment-retry:hover,
.flix-chat-widget__attachment-remove:hover {
  background: rgba(255, 255, 255, .1);
  color: var(--fcw-text);
}

.flix-chat-widget__attachment-retry[hidden],
.flix-chat-widget__attachment-remove[hidden] {
  display: none;
}

.flix-chat-widget__message-attachment-interactive {
  cursor: pointer;
}

.flix-chat-widget__message-attachment-interactive:focus-visible {
  outline: 2px solid var(--flix-chat-accent);
  outline-offset: 2px;
}

.flix-chat-widget__message-attachment-image {
  max-width: 220px;
  max-height: 220px;
  border-radius: var(--fcw-radius-bubble);
  border-end-start-radius: var(--fcw-radius-bubble-tail);
  display: block;
  cursor: pointer;
}

.flix-chat-widget__message--customer .flix-chat-widget__message-attachment-image {
  border-end-start-radius: var(--fcw-radius-bubble);
  border-end-end-radius: var(--fcw-radius-bubble-tail);
}

.flix-chat-widget__message-attachment-video {
  max-width: 260px;
  max-height: 260px;
  width: 100%;
  border-radius: var(--fcw-radius-bubble);
  border-end-start-radius: var(--fcw-radius-bubble-tail);
  display: block;
  background: #000;
}

.flix-chat-widget__message--customer .flix-chat-widget__message-attachment-video {
  border-end-start-radius: var(--fcw-radius-bubble);
  border-end-end-radius: var(--fcw-radius-bubble-tail);
}

/* ===== Image lightbox ===== */

.flix-chat-widget__lightbox {
  position: fixed;
  inset: 0;
  z-index: var(--fcw-z-dialog);
  background: rgba(2, 2, 2, .78);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: flix-chat-fade-rise var(--fcw-motion-panel) ease both;
}

.flix-chat-widget__lightbox-image {
  max-width: 92vw;
  max-height: 82vh;
  border-radius: var(--fcw-radius-md);
  box-shadow: var(--fcw-shadow-e3);
}

.flix-chat-widget__lightbox-close {
  position: absolute;
  top: 18px;
  inset-inline-end: 18px;
  width: 36px;
  height: 36px;
  border-radius: var(--fcw-radius-sm);
  background: rgba(255, 255, 255, .1);
  border: none;
  color: var(--fcw-text);
  display: grid;
  place-items: center;
  cursor: pointer;
  font-size: 19px;
}

/* ===== Composer ===== */

.flix-chat-widget__composer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-sizing: border-box;
  padding: 11px 11px 9px;
}

.flix-chat-widget__composer-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.flix-chat-widget__composer-attachments[hidden] {
  display: none;
}

.flix-chat-widget__composer-attachments .flix-chat-widget__attachment {
  min-width: 0;
  padding: 6px 8px;
}

.flix-chat-widget__composer-error {
  color: var(--fcw-danger);
  font-size: 12px;
  margin: 0;
}

.flix-chat-widget__composer-error[hidden] {
  display: none;
}

.flix-chat-widget__composer-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  background: var(--fcw-surface-input);
  border: 1px solid var(--fcw-border-composer);
  border-radius: var(--fcw-radius-bubble);
  padding: 5px;
  padding-inline-start: 12px;
  transition: border-color var(--fcw-motion-base), box-shadow var(--fcw-motion-base);
}

.flix-chat-widget__composer-row:focus-within {
  border-color: rgba(245, 130, 30, .5);
  box-shadow: 0 0 0 3px rgba(245, 130, 30, .1);
}

.flix-chat-widget__composer-input {
  flex: 1 1 auto;
  box-sizing: border-box;
  resize: none;
  font: 400 14px/1.5 Inter, sans-serif;
  color: var(--fcw-text);
  padding: 9px 0;
  max-height: 92px;
  background: transparent;
}

.flix-chat-widget__composer-input::placeholder {
  color: var(--fcw-text-faint);
}

.flix-chat-widget__composer-emoji,
.flix-chat-widget__composer-attach {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: var(--fcw-radius-sm);
  background: transparent;
  border: none;
  color: #7a716a;
  display: grid;
  place-items: center;
  font-size: 20px;
  cursor: pointer;
  transition: background var(--fcw-motion-fast), color var(--fcw-motion-fast);
}

.flix-chat-widget__composer-emoji:hover,
.flix-chat-widget__composer-attach:hover {
  color: var(--fcw-text-softer);
  background: rgba(255, 255, 255, .05);
}

.flix-chat-widget__composer-reply-preview[hidden],
.flix-chat-widget__composer-emoji[hidden] {
  display: none;
}

.flix-chat-widget__composer-send {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: var(--fcw-radius-composer-send);
  border: none;
  display: grid;
  place-items: center;
  font-size: 19px;
  cursor: pointer;
  background: linear-gradient(150deg, var(--flix-chat-accent), var(--fcw-accent-2));
  color: var(--fcw-on-accent);
  box-shadow: var(--fcw-shadow-accent);
  transition: transform var(--fcw-motion-fast), box-shadow var(--fcw-motion-fast), background var(--fcw-motion-fast);
}

.flix-chat-widget__composer-send:hover:not(:disabled) {
  transform: scale(1.04);
  box-shadow: var(--fcw-shadow-accent-hover);
}

.flix-chat-widget__composer-send:disabled {
  background: var(--fcw-accent-disabled-bg-composer);
  color: var(--fcw-accent-disabled-fg);
  box-shadow: none;
}

.flix-chat-widget__composer-footer-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-top: 8px;
  font: 500 11px/1 Inter, sans-serif;
  color: var(--fcw-text-faint);
}

.flix-chat-widget__composer-footer-note strong {
  color: var(--fcw-text-muted);
  font-weight: 700;
}

.flix-chat-widget button:disabled,
.flix-chat-widget textarea:disabled,
.flix-chat-widget input:disabled {
  cursor: not-allowed;
  opacity: .6;
}

.flix-chat-widget button:focus-visible,
.flix-chat-widget textarea:focus-visible,
.flix-chat-widget input:focus-visible,
.flix-chat-widget a:focus-visible {
  outline: 2px solid var(--flix-chat-accent);
  outline-offset: 2px;
}

/* ===== Customer info panel ===== */

.flix-chat-widget__customer-info {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--fcw-surface-2);
}

.flix-chat-widget__customer-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 12px 13px 8px;
  background: var(--fcw-surface-header);
  border-bottom: 1px solid var(--fcw-border);
  flex-shrink: 0;
}

.flix-chat-widget__customer-info-back {
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: var(--fcw-radius-md);
  background: transparent;
  border: none;
  color: var(--fcw-text-softer);
  cursor: pointer;
  font-size: 18px;
}

.flix-chat-widget__customer-info-back:hover {
  background: rgba(255, 255, 255, .06);
}

.flix-chat-widget__customer-info-header-title {
  font: 600 14px/1 Inter, sans-serif;
}

.flix-chat-widget__customer-info-body {
  flex: 1 1 auto;
  overflow-y: auto;
}

.flix-chat-widget__customer-info-placeholder {
  padding: 24px;
  text-align: center;
  color: var(--fcw-text-muted);
  font-size: 13px;
}

.flix-chat-widget__customer-info-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 20px 20px;
  border-bottom: 1px solid var(--fcw-border);
  gap: 4px;
}

.flix-chat-widget__customer-info-avatar {
  width: 64px;
  height: 64px;
  border-radius: var(--fcw-radius-full);
  background: linear-gradient(150deg, #3a2f27, #211a15);
  color: #F5b070;
  display: grid;
  place-items: center;
  font: 700 24px/1 Inter, sans-serif;
  margin-bottom: 8px;
}

.flix-chat-widget__customer-info-name {
  font: 700 17px/1.2 Inter, sans-serif;
}

.flix-chat-widget__customer-info-email {
  font-size: 13px;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__customer-info-active-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 600 11px/1 Inter, sans-serif;
  color: var(--fcw-success);
  background: rgba(63, 191, 110, .12);
  border: 1px solid rgba(63, 191, 110, .22);
  padding: 6px 11px;
  border-radius: var(--fcw-radius-full);
  margin-top: 6px;
}

.flix-chat-widget__customer-info-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--fcw-border);
  border-bottom: 1px solid var(--fcw-border);
}

.flix-chat-widget__customer-info-stat {
  background: var(--fcw-surface-2);
  padding: 16px;
  text-align: center;
}

.flix-chat-widget__customer-info-stat-value {
  font: 700 22px/1 Inter, sans-serif;
}

.flix-chat-widget__customer-info-stat-label {
  font: 500 11px/1 Inter, sans-serif;
  color: var(--fcw-text-muted);
  margin-top: 6px;
}

.flix-chat-widget__customer-info-section {
  padding: 16px;
}

.flix-chat-widget__customer-info-section-title {
  font: 700 10px/1 Inter, sans-serif;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--fcw-text-mutest);
  margin-bottom: 10px;
}

.flix-chat-widget__customer-info-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 0;
  border-bottom: 1px solid rgba(255, 255, 255, .04);
  font-size: 13px;
}

.flix-chat-widget__customer-info-row-label {
  flex: 1;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__customer-info-row-value {
  font-weight: 600;
  color: var(--fcw-text-soft);
}

.flix-chat-widget__customer-info-order-card {
  background: var(--fcw-surface-raised);
  border: 1px solid var(--fcw-border);
  border-radius: var(--fcw-radius-lg);
  padding: 13px;
}

.flix-chat-widget__customer-info-order-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font: 600 13px/1 Inter, sans-serif;
}

.flix-chat-widget__customer-info-order-status {
  font: 600 10px/1 Inter, sans-serif;
  color: var(--fcw-success);
  background: rgba(63, 191, 110, .12);
  padding: 5px 8px;
  border-radius: var(--fcw-radius-full);
}

.flix-chat-widget__customer-info-order-description {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__customer-info-order-date {
  font-size: 11px;
  color: var(--fcw-text-faint);
  margin-top: 6px;
}

/* ===== Responsive ===== */

@media (max-width: 1024px) {
  .flix-chat-widget__panel {
    width: 360px;
  }
}

@media (max-width: 480px) {
  .flix-chat-widget__panel {
    left: 8px;
    right: 8px;
    bottom: 8px;
    top: 8px;
    width: auto;
    height: auto;
    max-height: none;
  }
}

/* ===== Recent conversations (home screen) ===== */

.flix-chat-widget__recent {
  width: 100%;
  margin-top: 26px;
  text-align: start;
}

.flix-chat-widget__recent-title {
  font: 700 10px/1 Inter, sans-serif;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--fcw-text-mutest);
  margin-bottom: 6px;
  padding: 0 8px;
}

.flix-chat-widget__recent-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.flix-chat-widget__history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 11px 8px;
  border-radius: var(--fcw-radius-md);
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: start;
  transition: background var(--fcw-motion-fast);
}

.flix-chat-widget__history-item:hover {
  background: rgba(255, 255, 255, .05);
}

.flix-chat-widget__history-item--unread {
  background: var(--fcw-accent-soft-bg);
}

.flix-chat-widget__history-item--unread:hover {
  background: var(--fcw-accent-soft-bg);
}

.flix-chat-widget__history-item-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.flix-chat-widget__history-item-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.flix-chat-widget__history-item-unread-dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: var(--fcw-radius-full);
  background: var(--flix-chat-accent);
}

.flix-chat-widget__history-item-title {
  font: 600 13px/1.3 Inter, sans-serif;
  color: var(--fcw-text-soft);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

.flix-chat-widget__history-item-date {
  font: 400 11px/1.3 Inter, sans-serif;
  color: var(--fcw-text-muted);
}

.flix-chat-widget__history-item-status {
  flex: none;
  font: 600 10px/1 Inter, sans-serif;
  padding: 5px 9px;
  border-radius: var(--fcw-radius-full);
  text-transform: uppercase;
  letter-spacing: .02em;
}

.flix-chat-widget__history-item-status--open {
  color: var(--fcw-success);
  background: rgba(63, 191, 110, .12);
}

.flix-chat-widget__history-item-status--closed {
  color: var(--fcw-text-muted);
  background: rgba(255, 255, 255, .06);
}

.flix-chat-widget__history-viewer {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.flix-chat-widget__history-viewer .flix-chat-widget__messages {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
}

.flix-chat-widget__history-viewer-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 14px;
  background: rgba(255, 255, 255, .04);
  border-bottom: 1px solid var(--fcw-border);
  font: 500 12px/1.3 Inter, sans-serif;
  color: var(--fcw-text-muted);
  flex-shrink: 0;
}

.flix-chat-widget__history-viewer-banner-back {
  border: none;
  background: transparent;
  color: var(--flix-chat-accent);
  font: 600 12px/1 Inter, sans-serif;
  cursor: pointer;
  padding: 0;
}

.flix-chat-widget__history-viewer-banner-back:hover {
  text-decoration: underline;
}

/* ===== RTL (Arabic) ===== */

/* Letter-spacing visually breaks connected Arabic script, so the few
   uppercase-tracked labels drop it in RTL. */
.flix-chat-widget[dir='rtl'] .flix-chat-widget__recent-title,
.flix-chat-widget[dir='rtl'] .flix-chat-widget__date-divider-label,
.flix-chat-widget[dir='rtl'] .flix-chat-widget__history-item-status {
  letter-spacing: 0;
}

/* The send arrow points in the reading direction. */
.flix-chat-widget[dir='rtl'] .flix-chat-widget__composer-send .flix-chat-widget__icon {
  transform: scaleX(-1);
}

/* ===== Reduced motion ===== */

/* Standard blanket override: collapses every animation/transition in the
   widget to effectively instant for anyone with this OS/browser
   accessibility preference set. Scoped to .flix-chat-widget so it can
   never affect the host page. Untouched (full animations) for everyone
   else — this block only ever applies inside the media query. */
@media (prefers-reduced-motion: reduce) {
  .flix-chat-widget,
  .flix-chat-widget *,
  .flix-chat-widget *::before,
  .flix-chat-widget *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
`;

export function injectWidgetStyles(): void {
  if (document.getElementById(STYLE_ELEMENT_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = WIDGET_STYLES;
  document.head.append(style);
}
